import { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Loader2, Landmark, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { fmtCLP, PRIORIDAD_LABEL, MODOS } from './egresosUtils';

const PRIO_CLS = { 1: 'bg-emerald-100 text-emerald-800', 2: 'bg-sky-100 text-sky-800', 3: 'bg-orange-100 text-orange-800', 4: 'bg-gray-100 text-gray-600' };

export function EgresoConciliarDialog({ egreso, modo = 'egreso', onOpenChange, onDone }) {
  const M = MODOS[modo];
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!egreso) return;
    setSel(null); setNota(''); setLoading(true);
    api.get(`${M.base}/${egreso.id}/candidatos-cartola`)
      .then((r) => {
        const lista = r.data.candidatos || [];
        setCands(lista);
        const pref = lista.find((c) => c.id === egreso.movimiento_candidato_id) || (lista[0]?.prioridad <= 2 ? lista[0] : null);
        setSel(pref?.id || null);
      })
      .catch(() => setCands([]))
      .finally(() => setLoading(false));
  }, [egreso, M.base]);

  const conciliar = async () => {
    if (!sel) { toast.error('Selecciona el movimiento de la cartola'); return; }
    setGuardando(true);
    try {
      const r = await api.post(`${M.base}/${egreso.id}/conciliar-banco`, { movimiento_id: sel, nota });
      toast.success(`${M.label} ${egreso.codigo_conciliacion} conciliado con la cartola (${PRIORIDAD_LABEL[r.data.prioridad] || r.data.prioridad})`);
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error conciliando'); }
    finally { setGuardando(false); }
  };

  const descartar = async () => {
    try {
      await api.post(`${M.base}/${egreso.id}/descartar-candidato`);
      toast.success(`Candidato descartado: el ${M.label.toLowerCase()} vuelve a pendiente de conciliación`);
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  return (
    <Dialog open={!!egreso} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="modal-conciliar-egreso">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-sky-600" /> Conciliar {M.label.toLowerCase()} con la cartola</DialogTitle>
          <DialogDescription>
            {egreso && <>
              <span className="font-mono">{egreso.codigo_conciliacion}</span> · {egreso.razon_social} · <b>{fmtCLP(egreso.monto)}</b> · {modo === 'ingreso' ? 'cobrado' : 'pagado'} {fmtFecha(egreso.fecha_pago)}
              {egreso.fecha_cartola_esperada && <> · esperado en cartola <b>{fmtFecha(egreso.fecha_cartola_esperada)}</b></>}
              <br />Nombre de conciliación: <span className="font-mono">{egreso.nombre_conciliacion}</span>. Esta acción no modifica la aplicación a facturas.
            </>}
          </DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Buscando {M.montoMov === 'abono' ? 'abonos' : 'cargos'} con el mismo monto…</div> : (
          <div className="max-h-[50vh] overflow-y-auto rounded border" data-testid="lista-candidatos-cartola">
            {cands.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No hay {M.montoMov === 'abono' ? 'abonos' : 'cargos'} pendientes en la cartola por {egreso && fmtCLP(egreso.monto)}. Sube la cartola del período o revisa el monto.</p>}
            {cands.map((c) => (
              <label key={c.id} className={`flex items-start gap-3 border-b last:border-0 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${sel === c.id ? 'bg-sky-50' : ''}`} data-testid={`candidato-${c.id}`}>
                <input type="radio" name="cand" checked={sel === c.id} onChange={() => setSel(c.id)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{fmtFecha(c.fecha)}</span>
                    <span className="truncate" title={c.detalle}>{c.detalle}</span>
                    {c.docto && <span className="text-[10px] text-muted-foreground">doc {c.docto}</span>}
                  </div>
                  <span className={`inline-block mt-1 rounded px-1.5 py-0.5 text-[10px] ${PRIO_CLS[c.prioridad] || PRIO_CLS[4]}`}>
                    {PRIORIDAD_LABEL[c.prioridad] || 'Monto igual, fuera de ventana'}
                  </span>
                  {egreso?.movimiento_candidato_id === c.id && <span className="ml-2 text-[10px] text-orange-700">← candidato sugerido por el match automático</span>}
                </div>
                <span className={`font-mono text-xs whitespace-nowrap ${M.colorMonto}`}>{fmtCLP(c[M.montoMov])}</span>
              </label>
            ))}
          </div>
        )}
        <div>
          <Label className="text-xs">Nota (opcional)</Label>
          <Input value={nota} onChange={(e) => setNota(e.target.value)} className="text-sm" data-testid="input-nota-conciliar" />
        </div>
        <DialogFooter className="gap-2">
          {egreso?.estado_conciliacion_banco === 'observado' && (
            <Button variant="outline" className="text-orange-700 border-orange-300 mr-auto" onClick={descartar} data-testid="btn-descartar-candidato">
              <XCircle className="h-4 w-4 mr-1" /> Descartar candidato
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cerrar</Button>
          <Button onClick={conciliar} disabled={guardando || !sel} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-confirmar-conciliar">
            {guardando ? 'Conciliando…' : 'Conciliar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
